import { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import "./Tools.css";
import BeforeAfterToolCard from "../tools/BeforeAfterToolCard";
import Pricing from "../../components/popups/pricing/Pricing";
import CategorySeeAllModal from "./CategorySeeAllModal";
import TrendingToolUploadModal from "./TrendingToolUploadModal";
import DashboardToolPrepOverlay from "./DashboardToolPrepOverlay";
import { ToolsPageSkeleton } from "./DashboardCatalogSkeleton";
import { useImageContext } from "../../contexts/ImageContext";
import { clearEditorSession } from "../../state/editorStore";
import {
  ENTRY_SOURCE,
  writeToolPrepSession,
  clearToolPrepSession,
  isToolPrepKey,
} from "../../utils/editorEntrySession";
import { prepareDashboardToolPrep } from "./navigateToToolPrep";
import { getDashboardTools } from "../../services/api/catalog/dashboard_tools";
import { getResizePresets } from "../../services/api/catalog/resize_presets";
import { getPassportPresets } from "../../services/api/catalog/passport_presets";
import { fetchAccountBootstrap } from "../../services/api/app/accountBootstrap";
import { PASSPORT_CATEGORY_ID } from "../../utils/passportPresetUtils";
import {
  mapApiToolToCardItem,
  TRENDING_TOOL_IDS,
  AI_TOOL_IDS,
  mergeDashboardToolSections,
  isAiToolComingSoon,
  sortToolsAvailableFirst,
} from "../../config/dashboard/trendingToolsConfig";
import recolorBefore from "../../assets/before_after/blackcar1.jpeg";
import recolorAfter from "../../assets/before_after/redcar1.jpeg";
import upscaleComposite from "../../assets/before_after/image_enhancer_s_e486481843.webp";
import {
  flattenPresetsForTrendingTool,
  getTrendingToolPresetCategoryIds,
} from "../../config/dashboard/trendingToolPresetConfig";
import {
  resolveDemoSample,
  DEMO_SAMPLE_POOL_KEYS,
} from "../../config/dashboard/demoAssetsConfig";
import { registerLibraryUploadOnce } from "../../utils/registerLibraryUploadOnce";
import { CREDITS_BALANCE_REFRESH_EVENT } from "../../config/app/signupCredits";
import crownProIcon from "../../assets/pro_icons/Crown1.png";
import ImagePickerModal from "../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../hooks/app/useImagePickerModal";
import { openLibraryItemInEditor } from "../../utils/openLibraryInEditor";
import { usePricingCatalog } from "../../hooks/app/usePricingCatalog";
import {
  insufficientCreditsBanner,
  resolveToolCreditCost,
  shouldForceCreditsGate,
  toolRequiresAiCredits,
} from "../../utils/credits/dashboardAiCredits";

/** Tools page section order: Trending → AI Tools → AI Photo Studio → Document Tools → Editing Tools → Image Conversion */
const TOOLS_SECTION_ORDER = [
  "trending",
  "ai_tools",
  "ai_photo_studio",
  "document_tools",
  "editing_tools",
  "image_conversion",
];

function withProIcon(item, categorySlug) {
  if (!item) return null;
  if (categorySlug === "ai_tools" || categorySlug === "ai_photo_studio") {
    return { ...item, proIcon: crownProIcon };
  }
  return item;
}

const Tools = () => {
  const [toolSections, setToolSections] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingBannerMessage, setPricingBannerMessage] = useState(null);
  const [userCredits, setUserCredits] = useState(null);
  const [activeTrendingTool, setActiveTrendingTool] = useState(null);
  const [trendingPresets, setTrendingPresets] = useState([]);
  const [trendingPresetsLoading, setTrendingPresetsLoading] = useState(false);
  const [passportTrendingPresets, setPassportTrendingPresets] = useState([]);
  const [passportTrendingPresetsLoading, setPassportTrendingPresetsLoading] =
    useState(false);
  const [toolPrepKey, setToolPrepKey] = useState(null);

  const pendingTrendingPresetRef = useRef(null);
  const pendingEditingPanelRef = useRef(null);
  const pendingPassportPresetRef = useRef(null);

  const navigate = useNavigate();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const { getToken, isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { costFor } = usePricingCatalog();
  const {
    setFile,
    setNormalEntry,
    clearImage,
    setEntrySource,
    startPassportFlow,
    startTrendingEntryFlow,
  } = useImageContext();

  const openToolPrepOverlay = useCallback(
    (toolKey, { clearSource = true } = {}) => {
      if (!isToolPrepKey(toolKey) || isAiToolComingSoon(toolKey)) return;
      const ok = prepareDashboardToolPrep({
        toolPrep: toolKey,
        setNormalEntry,
        clearImage,
        writeToolPrepSession,
        clearEditorSession,
        clearSource,
      });
      if (ok) setToolPrepKey(toolKey);
    },
    [clearImage, setNormalEntry]
  );

  const closeToolPrepOverlay = useCallback(() => {
    setToolPrepKey(null);
  }, []);

  const navigateToEditor = (state = {}) => {
    navigate("/editor", { state });
  };

  const kickoffLibraryUpload = (file) => {
    if (!file) return;
    void (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        await registerLibraryUploadOnce(token, file);
      } catch (err) {
        console.warn("[Tools] library upload register failed", err);
      }
    })();
  };

  const forceOutOfCreditsPopup =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_FORCE_OUT_OF_CREDITS_POPUP === "true";

  const mockZeroCredits =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_MOCK_ZERO_CREDITS === "true";

  useEffect(() => {
    let cancelled = false;
    setToolsLoading(true);
    getDashboardTools({
      dashboardOnly: true,
      slugs: TOOLS_SECTION_ORDER,
    })
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const ordered = mergeDashboardToolSections([...res.data]).sort(
            (a, b) =>
              TOOLS_SECTION_ORDER.indexOf(a.slug) -
              TOOLS_SECTION_ORDER.indexOf(b.slug)
          );
          setToolSections(ordered);
        } else {
          setToolSections([]);
        }
      })
      .catch(() => {
        if (!cancelled) setToolSections([]);
      })
      .finally(() => {
        if (!cancelled) setToolsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) {
      setUserCredits(null);
      return;
    }
    let cancelled = false;
    const fetchCredits = async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const data = await fetchAccountBootstrap(token);
        if (!cancelled) setUserCredits(data?.credits ?? 0);
      } catch {
        if (!cancelled) setUserCredits(null);
      }
    };
    fetchCredits();
    return () => {
      cancelled = true;
    };
  }, [isAuthLoaded, isSignedIn, getToken]);

  useEffect(() => {
    const onCreditsRefresh = (event) => {
      const balance = event?.detail?.balance;
      if (typeof balance === "number") {
        setUserCredits(balance);
        return;
      }
      (async () => {
        try {
          const token = await getToken();
          if (!token) return;
          const data = await fetchAccountBootstrap(token, { force: true });
          setUserCredits(data?.credits ?? 0);
        } catch {
          // keep last known
        }
      })();
    };
    window.addEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
    return () =>
      window.removeEventListener(CREDITS_BALANCE_REFRESH_EVENT, onCreditsRefresh);
  }, [getToken]);

  const openInsufficientCreditsPricing = (toolLabel) => {
    setPricingBannerMessage(insufficientCreditsBanner(toolLabel));
    setShowPricingModal(true);
  };

  const refreshCreditsBalance = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const data = await fetchAccountBootstrap(token, { force: true });
      setUserCredits(data?.credits ?? 0);
    } catch {
      // keep last known
    }
  };

  /** AI / trending / studio only — editing + document tools are free. */
  const guardAiCredits = async (toolOrKey, toolLabel) => {
    if (
      forceOutOfCreditsPopup ||
      mockZeroCredits ||
      shouldForceCreditsGate(toolOrKey)
    ) {
      openInsufficientCreditsPricing(toolLabel);
      return false;
    }
    const needed = resolveToolCreditCost(costFor, toolOrKey);
    let credits = userCredits;
    if (credits === null || credits < needed) {
      try {
        const token = await getToken();
        if (token) {
          const data = await fetchAccountBootstrap(token);
          credits = data?.credits ?? 0;
          setUserCredits(credits);
        }
      } catch {
        return true;
      }
    }
    if (credits !== null && credits < needed) {
      openInsufficientCreditsPricing(toolLabel);
      return false;
    }
    return true;
  };

  const guardToolEntry = async (tool) => {
    if (!toolRequiresAiCredits(tool)) return true;
    const label =
      tool?.cardLabel || tool?.label || tool?.modalTitle || tool?.name;
    return guardAiCredits(tool, label);
  };

  const activeTrendingToolId = activeTrendingTool?.id ?? null;
  const isPassportTrendingModal =
    activeTrendingToolId === TRENDING_TOOL_IDS.PASSPORT_PHOTOS;

  useEffect(() => {
    if (!activeTrendingToolId) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);
      setPassportTrendingPresets([]);
      setPassportTrendingPresetsLoading(false);
      return;
    }

    if (isPassportTrendingModal) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);
      let cancelled = false;
      setPassportTrendingPresetsLoading(true);
      getPassportPresets()
        .then((res) => {
          if (cancelled) return;
          if (res?.success && Array.isArray(res.data)) {
            const match = res.data.find(
              (c) => c.category_id === PASSPORT_CATEGORY_ID
            );
            setPassportTrendingPresets(
              match?.presets ?? res.data[0]?.presets ?? []
            );
          } else {
            setPassportTrendingPresets([]);
          }
        })
        .catch(() => {
          if (!cancelled) setPassportTrendingPresets([]);
        })
        .finally(() => {
          if (!cancelled) setPassportTrendingPresetsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setPassportTrendingPresets([]);
    setPassportTrendingPresetsLoading(false);

    const categoryIds =
      activeTrendingTool?.presetCategoryIds ||
      getTrendingToolPresetCategoryIds(activeTrendingToolId);
    if (!categoryIds?.length) {
      setTrendingPresets([]);
      setTrendingPresetsLoading(false);
      return;
    }

    let cancelled = false;
    setTrendingPresetsLoading(true);
    getResizePresets({ categoryIds })
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setTrendingPresets(
            flattenPresetsForTrendingTool(res.data, categoryIds)
          );
        } else {
          setTrendingPresets([]);
        }
      })
      .catch(() => {
        if (!cancelled) setTrendingPresets([]);
      })
      .finally(() => {
        if (!cancelled) setTrendingPresetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTrendingToolId, activeTrendingTool, isPassportTrendingModal]);

  const handleTrendingToolSelect = (tool) => {
    if (!tool?.clickable) return;
    setActiveTrendingTool(tool);
  };

  const handleLibraryPick = (item) => {
    pendingPassportPresetRef.current = null;
    pendingTrendingPresetRef.current = null;
    pendingEditingPanelRef.current = null;
    setActiveTrendingTool(null);
    openLibraryItemInEditor(navigate, item, { setNormalEntry, clearImage });
  };

  const openPickerForFile = (onPickFile) => {
    openImagePicker({
      onPickFile,
      onPickLibrary: handleLibraryPick,
    });
  };

  const processEditingToolFile = async (file, editorPanel) => {
    if (!file) return;
    if (
      toolRequiresAiCredits(editorPanel) &&
      !(await guardAiCredits(editorPanel))
    ) {
      pendingEditingPanelRef.current = null;
      return;
    }
    try {
      clearEditorSession();
      flushSync(() => {
        setNormalEntry();
        setFile(file);
      });
      kickoffLibraryUpload(file);
      if (isToolPrepKey(editorPanel)) {
        openToolPrepOverlay(editorPanel, { clearSource: false });
      } else {
        clearToolPrepSession();
        navigateToEditor({
          entrySource: ENTRY_SOURCE.NORMAL,
          freshUpload: true,
          editorPanel: editorPanel || null,
          openEditorPanel: editorPanel || null,
        });
      }
    } catch (err) {
      console.error("Failed to process editing tool file", err);
      alert("Could not process the selected file. Please try a different image.");
    } finally {
      pendingEditingPanelRef.current = null;
    }
  };

  const handleEditingToolSelect = async (tool) => {
    if (!tool?.clickable) return;
    if (!(await guardToolEntry(tool))) return;
    pendingEditingPanelRef.current = tool.editorPanel || tool.toolKey || null;
    const editorPanel = pendingEditingPanelRef.current;

    // Prep-first: open tool popup; ImagePicker lives on the Original card.
    if (isToolPrepKey(editorPanel)) {
      pendingEditingPanelRef.current = null;
      try {
        openToolPrepOverlay(editorPanel, { clearSource: true });
      } catch (err) {
        console.error("Failed to open tool prep", err);
        alert("Could not open that tool. Please try again.");
      }
      return;
    }

    openPickerForFile((file) => processEditingToolFile(file, editorPanel));
  };

  const handleTrendingToolUpload = async (preset) => {
    if (
      !(await guardAiCredits(
        activeTrendingTool,
        activeTrendingTool?.cardLabel || activeTrendingTool?.modalTitle
      ))
    ) {
      return;
    }
    if (!activeTrendingTool?.entrySource) return;
    pendingTrendingPresetRef.current = preset || null;
    const entrySource = activeTrendingTool.entrySource;
    const pendingPreset = pendingTrendingPresetRef.current;
    openPickerForFile(async (file) => {
      if (
        !(await guardAiCredits(
          activeTrendingTool,
          activeTrendingTool?.cardLabel || activeTrendingTool?.modalTitle
        ))
      ) {
        pendingTrendingPresetRef.current = null;
        return;
      }
      try {
        setActiveTrendingTool(null);
        clearEditorSession();
        if (pendingPreset) {
          startTrendingEntryFlow(pendingPreset, entrySource);
        } else {
          setNormalEntry();
          setEntrySource(entrySource);
        }
        setFile(file);
        clearToolPrepSession();
        kickoffLibraryUpload(file);
        navigateToEditor({
          entrySource,
          entryResizePreset: pendingPreset || null,
          openResizePanel: Boolean(pendingPreset),
          freshUpload: true,
        });
      } catch (err) {
        console.error("Failed to process trending tool file", err);
        alert("Could not process the selected file. Please try a different image.");
      } finally {
        pendingTrendingPresetRef.current = null;
      }
    });
  };

  const handlePassportPresetSelect = async (preset) => {
    if (
      !(await guardAiCredits(
        TRENDING_TOOL_IDS.PASSPORT_PHOTOS,
        "Passport Photos"
      ))
    ) {
      return;
    }
    pendingPassportPresetRef.current = preset;
    openPickerForFile(async (file) => {
      const pendingPreset = pendingPassportPresetRef.current;
      if (!file || !pendingPreset) {
        pendingPassportPresetRef.current = null;
        return;
      }
      if (
        !(await guardAiCredits(
          TRENDING_TOOL_IDS.PASSPORT_PHOTOS,
          "Passport Photos"
        ))
      ) {
        pendingPassportPresetRef.current = null;
        return;
      }
      try {
        clearEditorSession();
        startPassportFlow(pendingPreset);
        setFile(file);
        kickoffLibraryUpload(file);
        navigateToEditor({
          entrySource: ENTRY_SOURCE.PASSPORT,
          editorFlow: "passport",
          passportPreset: pendingPreset,
          freshUpload: true,
        });
      } catch (err) {
        console.error("Failed to process passport file", err);
        alert("Could not process the selected file. Please try a different image.");
        setNormalEntry();
      } finally {
        pendingPassportPresetRef.current = null;
      }
    });
  };

  const handleSampleClick = (sampleOrSrc, options = {}) => {
    const sample = resolveDemoSample(sampleOrSrc);
    if (!sample) return;
    setActiveTrendingTool(null);
    clearEditorSession();
    clearImage();
    const isPassportSample =
      sample.category === DEMO_SAMPLE_POOL_KEYS.PASSPORT ||
      options.categoryId === PASSPORT_CATEGORY_ID;
    if (isPassportSample) {
      const preset =
        options.preset ||
        passportTrendingPresets.find((p) => p.is_default) ||
        passportTrendingPresets[0];
      if (preset) {
        startPassportFlow(preset);
      } else {
        setNormalEntry();
      }
      navigateToEditor({
        images: [sample.originalUrl],
        demoSample: true,
        freshUpload: true,
        entrySource: ENTRY_SOURCE.PASSPORT,
        editorFlow: "passport",
        passportPreset: preset || null,
      });
      return;
    }
    setNormalEntry();
    navigateToEditor({
      images: [sample.originalUrl],
      processedImage: sample.bgRemovedUrl,
      demoSample: true,
    });
  };

  const handleToolActivate = (item) => {
    if (!item?.clickable || !item?.clientTool) return;
    const slug = item.categorySlug;
    if (
      slug === "editing_tools" ||
      slug === "ai_tools" ||
      slug === "ai_photo_studio" ||
      slug === "document_tools" ||
      slug === "image_conversion"
    ) {
      handleEditingToolSelect(item.clientTool);
      return;
    }
    handleTrendingToolSelect(item.clientTool);
  };

  return (
    <div className="tools-page">
      <ImagePickerModal {...imagePickerProps} />

      <header className="tools-page__header">
        <h1 className="tools-page__title">Tools</h1>
        <p className="tools-page__subtitle">
          Trending, AI, document, editing, and image conversion tools — pick one to get started.
        </p>
      </header>

      {toolsLoading ? (
        <ToolsPageSkeleton />
      ) : toolSections.length === 0 ? (
        <div className="tools-page__loading">
          <p>Could not load tools. Please try again later.</p>
        </div>
      ) : (
        toolSections.map((section) => {
          const tools = sortToolsAvailableFirst(
            (section.tools || [])
              .map((t) =>
                withProIcon(mapApiToolToCardItem(t, section.slug), section.slug)
              )
              .filter(Boolean)
          );
          return (
            <section
              key={section.category_id || section.slug}
              className="tools-page__section"
            >
              <div className="tools-page__section-header">
                <h2 className="tools-page__section-title">{section.name}</h2>
                {section.description ? (
                  <p className="tools-page__section-desc">
                    {section.description}
                  </p>
                ) : null}
              </div>
              <div className="tools-page__grid">
                {tools.map((item) => (
                  <BeforeAfterToolCard
                    key={`${section.slug}-${item.toolKey}`}
                    label={item.label}
                    beforeImage={
                      item.toolKey === AI_TOOL_IDS.RECOLOR
                        ? recolorBefore
                        : item.toolKey === AI_TOOL_IDS.UPSCALE
                          ? upscaleComposite
                          : undefined
                    }
                    afterImage={
                      item.toolKey === AI_TOOL_IDS.RECOLOR
                        ? recolorAfter
                        : item.toolKey === AI_TOOL_IDS.UPSCALE
                          ? upscaleComposite
                          : undefined
                    }
                    clickable={Boolean(item.clickable)}
                    comingSoon={Boolean(item.comingSoon)}
                    proIcon={item.proIcon || null}
                    onClick={() => handleToolActivate(item)}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      <CategorySeeAllModal
        category={{
          category_id: PASSPORT_CATEGORY_ID,
          category_name: "Passport Photos",
        }}
        presets={passportTrendingPresets}
        isOpen={isPassportTrendingModal}
        loading={passportTrendingPresetsLoading}
        onClose={() => setActiveTrendingTool(null)}
        onUpload={(preset) => {
          setActiveTrendingTool(null);
          handlePassportPresetSelect(preset);
        }}
        onSampleClick={handleSampleClick}
      />

      <TrendingToolUploadModal
        tool={activeTrendingTool}
        isOpen={Boolean(activeTrendingTool) && !isPassportTrendingModal}
        onClose={() => setActiveTrendingTool(null)}
        onUpload={handleTrendingToolUpload}
        presets={trendingPresets}
        presetsLoading={trendingPresetsLoading}
        samplesClickable
        onSampleClick={handleSampleClick}
      />

      {showPricingModal && (
        <Pricing
          bannerMessage={pricingBannerMessage}
          onClose={async () => {
            setShowPricingModal(false);
            setPricingBannerMessage(null);
            await refreshCreditsBalance();
          }}
          onCreditsUpdated={refreshCreditsBalance}
        />
      )}

      {toolPrepKey ? (
        <DashboardToolPrepOverlay
          toolKey={toolPrepKey}
          onClose={closeToolPrepOverlay}
        />
      ) : null}
    </div>
  );
};

export default Tools;
